import { useEffect, useState, type CSSProperties } from 'react';
import { DIE_FACES, t } from '@kadice/shared';
import { anim } from '../speed';

/**
 * Dice are drawn from the art in `/img/anim/dice/` (`result N.png` once landed, the `roll N.png`
 * cels looping while a die rolls). Flip to true to go back to the old CSS pip faces everywhere.
 * The pip faces stay in use for Portrait Layout's column (`<Dice pips>`). TODO: once satisfied
 * with the image dice, drop the flag (keep `PIPS`, `LegacyPips`, `.pip` and the die box rules).
 */
export const LEGACY_PIP_DICE = false;

const DIE_ART = '/img/anim/dice/';
/** Number of `roll N.png` cels. */
const ROLL_CELS = 12;
/** How long each roll cel shows (about 12 fps: one full spin a second, a die's roll time). */
const CEL_MS = 83;
const faceSrc = (value: number) => `${DIE_ART}result ${value}.png`;
const celSrc = (cel: number) => `${DIE_ART}roll ${cel + 1}.png`;

// Image dice: `html.img-dice` switches the die CSS to the art; the images load up front so the
// first roll doesn't flicker.
if (!LEGACY_PIP_DICE && typeof document !== 'undefined') {
  document.documentElement.classList.add('img-dice');
  for (let v = 1; v <= DIE_FACES; v++) new Image().src = faceSrc(v);
  for (let c = 0; c < ROLL_CELS; c++) new Image().src = celSrc(c);
}

/**
 * CSS pip dice (`LEGACY_PIP_DICE`, `<Dice pips>`).
 * Pip positions on a 3×3 grid (0 = top-left, 8 = bottom-right), as on a real die.
 */
const PIPS: Record<number, readonly number[]> = {
  1: [4],
  2: [2, 6],
  3: [2, 4, 6],
  4: [0, 2, 6, 8],
  5: [0, 2, 4, 6, 8],
  6: [0, 2, 3, 5, 6, 8],
};

/** CSS pip face: nine grid cells, the right ones filled. */
function LegacyPips({ value }: { value: number }) {
  const on = PIPS[value] ?? [];
  return (
    <>
      {Array.from({ length: 9 }, (_, i) => (
        <span key={i} className={on.includes(i) ? 'pip' : undefined} />
      ))}
    </>
  );
}

/** The roll cels, looping, each starting where the clock is (so dice rolling together stay in step). */
function RollCels() {
  const celMs = anim(CEL_MS);
  const now = () => Math.floor(performance.now() / celMs) % ROLL_CELS;
  const [cel, setCel] = useState(now);
  useEffect(() => {
    const id = setInterval(() => setCel(now()), celMs / 2);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [celMs]);
  return <DieArt src={celSrc(cel)} />;
}

/**
 * One piece of die art over the die's box (`.die-art`, sized so the drawn die fills the box). The
 * tint layer under it (`::before`, masked to the art) turns the white face gold for held dice.
 */
function DieArt({ src }: { src: string }) {
  return (
    <span className="die-art" style={{ '--die-src': `url('${src}')` } as CSSProperties} aria-hidden>
      <img src={src} alt="" draggable={false} />
    </span>
  );
}

/**
 * The face of a die (none for 0): its art, or the roll cels looping while `rolling`. Goes
 * inside an element with class `die`. (Legacy: the CSS pip grid, see `LEGACY_PIP_DICE`; `pips`
 * forces it, with class `pip-die` on the die.)
 */
export function Pips({ value, rolling, pips }: { value: number; rolling?: boolean; pips?: boolean }) {
  if (LEGACY_PIP_DICE || pips) return <LegacyPips value={value} />;
  if (rolling) return <RollCels />;
  return value >= 1 && value <= DIE_FACES ? <DieArt src={faceSrc(value)} /> : null;
}

/**
 * Read-only row of dice (held dice, war rolls). `held`: indexes coloured as held. `lifted`:
 * indexes shown picked out (a bot's announced keep). `dropping`: held dice being thrown back
 * before a roll (unlocked, falling away). `rolling`: indexes mid-roll (see `rolling.ts`). `slots`: always this many
 * places, the empty ones shown as faint outlines. `nudge`: a blinking ▲ or ▼ on one
 * die, sized to it as on your dice row (power examples: the die the power changes); `dir` null
 * keeps the arrow's room without showing one, so the die doesn't move when it goes; `pressed`
 * shows it pushed in (the power examples' press). `winning` /
 * `losing` (tutorial examples): dice that won blink green and grow; dice that lost shrink.
 * `pips`: the CSS pip faces instead of the art (Portrait Layout's column, where the art is too
 * small to read).
 */
export function Dice({
  values,
  small,
  held,
  dropping,
  lifted,
  rolling,
  slots,
  nudge,
  winning,
  losing,
  pips,
}: {
  values: readonly number[];
  small?: boolean;
  held?: readonly number[];
  dropping?: readonly number[];
  lifted?: readonly number[];
  rolling?: readonly number[];
  slots?: number;
  nudge?: { index: number; dir: 'up' | 'down' | null; pressed?: boolean };
  winning?: readonly number[];
  losing?: readonly number[];
  pips?: boolean;
}) {
  return (
    <div className={`dice${small ? ' small' : ''}`}>
      {values.map((v, i) =>
        v === 0 ? (
          // Not rolled yet (see `rolling.ts`): keeps its place, shows nothing.
          <span key={i} className="die" style={{ visibility: 'hidden' }} aria-hidden />
        ) : (
          <span
            key={i}
            className={`die${pips ? ' pip-die' : ''}${lifted?.includes(i) ? ' kept' : held?.includes(i) ? ' held' : ''}${rolling?.includes(i) ? ' rolling' : ''}${dropping?.includes(i) ? ' dropping' : ''}${nudge?.index === i ? ' nudged' : ''}${winning?.includes(i) ? ' winning' : losing?.includes(i) ? ' losing' : ''}`}
            role="img"
            aria-label={
              rolling?.includes(i)
                ? t('dice.rolling')
                : t(
                    dropping?.includes(i)
                      ? 'dice.droppedLabel'
                      : lifted?.includes(i)
                        ? 'dice.keptLabel'
                        : held?.includes(i)
                          ? 'dice.heldLabel'
                          : 'dice.showingLabel',
                    { value: v },
                  )
            }
          >
            <Pips value={v} rolling={rolling?.includes(i)} pips={pips} />
            {nudge?.index === i && nudge.dir && (
              <span className={`die-nudge ${nudge.dir}${nudge.pressed ? ' pressed' : ''}`} aria-hidden>
                {nudge.dir === 'up' ? '▲' : '▼'}
              </span>
            )}
          </span>
        ),
      )}
      {Array.from({ length: Math.max(0, (slots ?? 0) - values.length) }, (_, i) => (
        <span key={`empty-${i}`} className="die ghost" aria-hidden />
      ))}
    </div>
  );
}
